import { auth } from '../config/firebase';

export const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

/** The signed-in user's uid — the id the backend sees as req.user.uid — or null when signed out. */
export const currentUserUid = (): string | null => auth.currentUser?.uid ?? null;

// Get auth token from Firebase
// Automatically refreshes if expired (Firebase SDK handles this)
export const getAuthToken = async (): Promise<string> => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('Not authenticated');
  }

  try {
    // Firebase SDK automatically refreshes expired tokens
    // Using false (default) allows caching for better performance
    // The inactivity timeout hook handles force refresh after browser minimize
    const token = await user.getIdToken(false); // false = use cache if valid
    return token;
  } catch (error) {
    console.error('[API] Failed to get Firebase token:', error);
    // Retry with force refresh on error
    try {
      console.log('[API] Retrying with force refresh...');
      const refreshedToken = await user.getIdToken(true);
      return refreshedToken;
    } catch (retryError) {
      console.error('[API] Force refresh also failed:', retryError);
      throw new Error('Failed to authenticate');
    }
  }
};

/**
 * Error thrown by apiRequest for non-2xx responses: the HTTP status plus the
 * server's machine-readable `code` and the full JSON body, so callers can
 * branch on e.g. a 422 `cross_advertiser_include` and read its `orders[]`.
 * streamChatMessage throws the same shape, plus `retryAfterSeconds`.
 */
export interface ApiError extends Error {
  status: number;
  code?: string;
  body?: Record<string, any>;
  /** From Retry-After, when the response carried one the browser could read. */
  retryAfterSeconds?: number;
}

/**
 * Seconds to wait from a Retry-After header, in the delta-seconds form
 * express-rate-limit sends. Undefined when the header is missing or is not a
 * whole number of seconds. Cross-origin, the browser hides the header unless
 * the backend's CORS config exposes it.
 */
export const parseRetryAfter = (value: string | null): number | undefined => {
  const trimmed = value?.trim();
  return trimmed && /^\d+$/.test(trimmed) ? Number(trimmed) : undefined;
};

// Generic API request helper
export const apiRequest = async (
  endpoint: string,
  options: RequestInit = {}
): Promise<any> => {
  const token = await getAuthToken();

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      ...options.headers,
    },
  });

  if (!response.ok) {
    const body = await response.json().catch(() => ({ error: 'Request failed' }));
    const err = new Error(body?.error || `Request failed (${response.status})`) as ApiError;
    err.status = response.status;
    if (body && typeof body === 'object') {
      err.body = body;
      if (typeof body.code === 'string') err.code = body.code;
    }
    throw err;
  }

  return response.json();
};

const GOOGLE_STATUS_TTL_MS = 5 * 60 * 1000;
let googleStatusCache: { uid: string | null; value: any; fetchedAt: number } | null = null;

export const invalidateGoogleConnectionStatus = () => {
  googleStatusCache = null;
};

export const storeGoogleTokens = async (tokens: {
  access_token: string;
  refresh_token: string;
  expires_in: number;
}) => {
  const result = await apiRequest('/auth/google/store-tokens', {
    method: 'POST',
    body: JSON.stringify(tokens),
  });
  invalidateGoogleConnectionStatus();
  return result;
};

export const getGoogleConnectionStatus = async (options: { force?: boolean } = {}) => {
  const uid = auth.currentUser?.uid ?? null;
  const now = Date.now();
  if (
    !options.force &&
    googleStatusCache &&
    googleStatusCache.uid === uid &&
    now - googleStatusCache.fetchedAt < GOOGLE_STATUS_TTL_MS
  ) {
    return googleStatusCache.value;
  }
  const value = await apiRequest('/auth/google/status');
  googleStatusCache = { uid, value, fetchedAt: now };
  return value;
};

// Disconnect Google Workspace (clear tokens)
export const disconnectGoogle = async () => {
  const result = await apiRequest('/auth/google/disconnect', {
    method: 'POST',
  });
  invalidateGoogleConnectionStatus();
  return result;
};

// Slack user connection ---------------------------------------------------

export interface SlackConnectionStatus {
  connected: boolean;
  teamId?: string;
  teamName?: string;
  slackUserId?: string;
  scopes?: string[];
  connectedAt?: number;
}

export const getSlackConnectionStatus = async (): Promise<SlackConnectionStatus> => {
  return apiRequest('/auth/slack/status');
};

export const disconnectSlack = async () => {
  return apiRequest('/auth/slack/disconnect', { method: 'POST' });
};

/**
 * Open the Slack OAuth flow in a popup. Resolves only after the popup posts
 * `{type:'slack-connected', ok:true}` back to the opener. Rejects if the
 * popup reports failure, or if the user closes it before completing.
 */
export const connectSlackPopup = async (): Promise<void> => {
  const { url } = await apiRequest('/auth/slack/initiate', { method: 'POST' });
  const popup = window.open(url, 'slack-connect', 'width=600,height=720');
  if (!popup) throw new Error('Popup blocked. Please allow popups for this site.');
  return new Promise((resolve, reject) => {
    let settled = false;
    const cleanup = () => {
      settled = true;
      window.removeEventListener('message', listener);
      clearInterval(interval);
    };
    const listener = (event: MessageEvent) => {
      if (event.data?.type === 'slack-connected') {
        cleanup();
        if (event.data.ok) resolve();
        else reject(new Error('Slack connection failed'));
      }
    };
    window.addEventListener('message', listener);
    const interval = setInterval(() => {
      if (popup.closed && !settled) {
        cleanup();
        reject(new Error('Slack connection cancelled'));
      }
    }, 500);
  });
};

// Get chat history
export const getChatHistory = async (sessionId: string) => {
  return apiRequest(`/api/chat/history/${sessionId}`);
};

// Why the server refused to start a stream: the `error` from its JSON body when
// it has one (every 429 is `{ error, code: 'rate_limited' }`), else the generic
// message, so a proxy's HTML error page reads as before.
const chatStreamStartError = async (response: Response): Promise<ApiError> => {
  const body = await response.json().catch(() => null);
  const serverMessage = typeof body?.error === 'string' && body.error ? body.error : null;
  const err = new Error(serverMessage ?? 'Failed to start chat stream') as ApiError;
  err.status = response.status;
  if (body && typeof body === 'object') {
    err.body = body;
    if (typeof body.code === 'string') err.code = body.code;
  }
  const retryAfterSeconds = parseRetryAfter(response.headers.get('Retry-After'));
  if (retryAfterSeconds !== undefined) err.retryAfterSeconds = retryAfterSeconds;
  return err;
};

// Stream chat messages (Server-Sent Events)
// Now supports file attachments via FormData
export const streamChatMessage = (
  message: string,
  sessionId: string | null,
  onMessage: (data: any) => void,
  onError: (error: Error) => void,
  agent: 'claude' | 'openai' = 'claude',
  files?: File[],
  userMessageId?: string,  // Frontend-generated message ID for Firestore consistency
  agentProfile?: 'channel-intelligence'  // scoped CI "Ask" agent; omit for the full assistant
) => {
  return new Promise<void>(async (resolve, reject) => {
    try {
      const token = await getAuthToken();
      const endpoint = agent === 'openai' ? '/api/chat/stream-openai' : '/api/chat/stream';

      // Use FormData if files are attached, otherwise use JSON
      let body: FormData | string;
      let headers: HeadersInit = {
        'Authorization': `Bearer ${token}`,
      };

      if (files && files.length > 0) {
        // FormData for file uploads
        const formData = new FormData();
        formData.append('message', message);
        if (sessionId) {
          formData.append('sessionId', sessionId);
        }
        if (userMessageId) {
          formData.append('userMessageId', userMessageId);
        }
        if (agentProfile) {
          formData.append('agentProfile', agentProfile);
        }

        // Append each file
        files.forEach((file) => {
          formData.append('files', file);
        });

        body = formData;
        // Don't set Content-Type - browser will set it with boundary for multipart/form-data
      } else {
        // JSON for text-only messages
        headers['Content-Type'] = 'application/json';
        body = JSON.stringify({ message, sessionId, userMessageId, agentProfile });
      }

      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: 'POST',
        headers,
        body,
      });

      if (!response.ok) {
        throw await chatStreamStartError(response);
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();

      if (!reader) {
        throw new Error('No response body');
      }

      // Read stream with proper SSE buffering
      // SSE messages can be split across multiple network chunks, so we need to buffer
      // partial lines and only process complete messages (those ending with \n\n)
      let consecutiveParseErrors = 0;
      const MAX_CONSECUTIVE_ERRORS = 5;
      let buffer = ''; // Buffer for incomplete data across chunks

      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          // Process any remaining buffered data before closing
          if (buffer.trim()) {
            console.warn('[API] Stream ended with unbuffered data:', buffer.substring(0, 200));
          }
          resolve();
          break;
        }

        // Append new chunk to buffer (use stream mode to handle incomplete UTF-8 sequences)
        buffer += decoder.decode(value, { stream: true });

        // SSE messages are delimited by double newlines (\n\n)
        // Split on \n\n to get complete messages
        const messages = buffer.split('\n\n');

        // The last element might be incomplete (no trailing \n\n), so keep it in the buffer
        buffer = messages.pop() || '';

        for (const message of messages) {
          // Skip empty messages (from consecutive \n\n)
          if (!message.trim()) continue;

          // Each message can have multiple lines (e.g., "event: ...\ndata: ...")
          // We only care about lines starting with "data: "
          const lines = message.split('\n');

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const jsonString = line.substring(6);
                const data = JSON.parse(jsonString);
                onMessage(data);

                // Reset consecutive error counter on successful parse
                consecutiveParseErrors = 0;

                if (data.type === 'complete' || data.type === 'error') {
                  resolve();
                  return;
                }
              } catch (parseError) {
                // Track consecutive parse errors to detect corrupted streams
                consecutiveParseErrors++;

                console.warn('[API] Skipping malformed SSE line (parse error):', parseError);
                console.warn('[API] Raw line:', line.substring(0, 200) + (line.length > 200 ? '...' : ''));
                console.warn('[API] Consecutive errors:', consecutiveParseErrors);

                // If too many consecutive errors, the stream is likely corrupted
                if (consecutiveParseErrors >= MAX_CONSECUTIVE_ERRORS) {
                  console.error('[API] Too many consecutive parse errors, stream appears corrupted');
                  // Stop reading and settle, so the caller's `finally` releases its send lock.
                  // Resolve, not reject: onError has already set the banner, just as an
                  // in-band `error` event resolves after onMessage.
                  reader.cancel().catch(() => {});
                  onError(new Error('Stream data corrupted. Please try again.'));
                  resolve();
                  return;
                }

                // Otherwise continue reading - single parse failures can occur with keepalive comments, etc.
              }
            }
          }
        }
      }
    } catch (error) {
      onError(error as Error);
      reject(error);
    }
  });
};

// Stop active session
export const stopChatSession = async (sessionId: string, agent: 'claude' | 'openai' = 'claude') => {
  const endpoint = agent === 'openai' ? `/api/chat/stop-openai/${sessionId}` : `/api/chat/stop/${sessionId}`;
  return apiRequest(endpoint, {
    method: 'POST',
  });
};

// Check session status
export const getSessionStatus = async (sessionId: string) => {
  return apiRequest(`/api/chat/status/${sessionId}`);
};

// Clear chat session history
export const clearChatSession = async (sessionId: string, agent: 'claude' | 'openai' = 'claude') => {
  const endpoint = agent === 'openai' ? `/api/chat/clear-openai/${sessionId}` : `/api/chat/clear/${sessionId}`;
  return apiRequest(endpoint, {
    method: 'POST',
  });
};

// Get all chat sessions with pagination
export const getChatSessions = async (limit: number = 20, lastDocId?: string) => {
  const queryParams = new URLSearchParams({
    limit: limit.toString(),
    ...(lastDocId && { lastDocId })
  });
  return apiRequest(`/api/chat/sessions?${queryParams}`);
};

// Delete a chat session
export const deleteChatSession = async (sessionId: string) => {
  return apiRequest(`/api/chat/sessions/${sessionId}`, {
    method: 'DELETE',
  });
};

// ====================================
// SHARING API
// ====================================

// Get all Culture Genesis users (for sharing feature)
export const getCultureGenesisUsers = async () => {
  return apiRequest('/api/users/culture-genesis');
};

// Create a new share
export const createShare = async (
  toUserId: string,
  sessionId: string,
  messageIds: string[],
  notes?: string
) => {
  return apiRequest('/api/shares', {
    method: 'POST',
    body: JSON.stringify({
      toUserId,
      sessionId,
      messageIds,
      notes,
    }),
  });
};

// Get all shares received by current user
export const getReceivedShares = async () => {
  return apiRequest('/api/shares/received');
};

// Get a specific share with its messages
export const getShareMessages = async (shareId: string) => {
  return apiRequest(`/api/shares/${shareId}/messages`);
};

// Mark share as viewed
export const markShareViewed = async (shareId: string) => {
  return apiRequest(`/api/shares/${shareId}/viewed`, {
    method: 'PUT',
  });
};

// Delete a share
export const deleteShare = async (shareId: string) => {
  return apiRequest(`/api/shares/${shareId}`, {
    method: 'DELETE',
  });
};

// Get count of unread shares
export const getUnreadSharesCount = async () => {
  return apiRequest('/api/shares/unread-count');
};

// ====================================
// SHARE LINKS API (URL-based sharing)
// ====================================

// Create a new shareable link for a conversation
export const createShareLink = async (
  sessionId: string,
  messageIds: string[],
  title?: string,
  expiresAt?: Date
) => {
  return apiRequest('/api/share-links', {
    method: 'POST',
    body: JSON.stringify({
      sessionId,
      messageIds,
      title,
      expiresAt: expiresAt?.toISOString(),
    }),
  });
};

// Get share link with messages (authenticated)
export const getShareLink = async (shareLinkId: string) => {
  return apiRequest(`/api/share-links/${shareLinkId}`);
};

// Increment view count for share link
export const incrementShareLinkView = async (shareLinkId: string) => {
  return apiRequest(`/api/share-links/${shareLinkId}/view`, {
    method: 'POST',
  });
};

// Revoke a share link (only creator can revoke)
export const revokeShareLink = async (shareLinkId: string) => {
  return apiRequest(`/api/share-links/${shareLinkId}/revoke`, {
    method: 'PUT',
  });
};

// Fork a shared conversation to a new session
export const forkConversation = async (shareLinkId: string) => {
  return apiRequest(`/api/share-links/${shareLinkId}/fork`, {
    method: 'POST',
  });
};

// ====================================
// GEMINI IMAGE GENERATION API
// ====================================

// Save a generated image to Google Drive
export const saveImageToDrive = async (
  sessionId: string,
  image: { base64: string; mimeType: string; prompt: string }
): Promise<{ success: boolean; driveLink?: string; downloadLink?: string; fileId?: string; fileName?: string; error?: string }> => {
  return apiRequest('/api/chat/save-image-to-drive', {
    method: 'POST',
    body: JSON.stringify({
      base64: image.base64,
      mimeType: image.mimeType,
      prompt: image.prompt,
    }),
  });
};
